import { isNullOrUndefined, UTC, utc } from 'rewire-common';
import { createWatcherFn, DataSignal, property, version, WatcherTypeFn } from 'rewire-core';
import { arraySearch, Form, IFieldDefn, IFieldDefns, IFormContext } from 'rewire-ui';
import { IUdf, UdfDocumentType, UdfType } from '../DataTypes';
import DataMutations from '../graphql/Mutations';
import mainPageModel from '../main-navigation/MainPageModel';
import udfsSubscriptionService from '../services/UdfsSubscriptionService';
import MasterDetailModel from '../settings/MasterDetailModel';
import { createReferenceListWithEffectiveRange } from '../settings/UdfEditor';
import { YMDFormat } from '../utility/DateUtils';
import { createUdfSearch } from '../utility/Searchers';
import EditDialogModel, { AccessFn, TEditDeleteFn, TEditLoadFn, TEditSaveFn } from './EditDialogModel';
import { convertUdfsToId, convertUdfsToName, createUdfExpressionEditor } from './UdfExpressionField';
import { camelCase } from 'change-case';

export function getUdfDisplayValue(key: string, value: any): string | undefined {
  try {
    if (isNullOrUndefined(key) || isNullOrUndefined(value)) return undefined;

    if (key.endsWith(':d')) return YMDFormat(value);

    if (Array.isArray(value)) {
      return value.length ? value.map((v) => v.name || v).join(', ') : undefined;
    }

    return value.name || value;
  } catch (_) {
    console.log(`error retrieving field value for ${key} in ${JSON.stringify(value)}`);
    return undefined;
  }
}

export const UdfSchema = `
  id
  type
  name
  documentType
  displayName
  isSystem
  allowableValues
  hasEffective
  isList
`;

export const udfTypes = {
  text: 'Text',
  reference: 'Reference',
  date: 'Date',
  boolean: 'True or False',
  expression: 'Expression',
  float: 'Decimal',
  integer: 'Integer',
};

const expressionTypes = {
  text: 'Text',
  date: 'Date',
  boolean: 'True or False',
  float: 'Decimal',
  integer: 'Integer',
};

const expressionTypesToESType = {
  text: ':t',
  date: ':d',
  boolean: ':b',
  float: ':f',
  integer: ':i',
};

function udfNameToExpressionType(name: string): keyof typeof expressionTypes {
  const suffix = name.slice(-2);
  switch (suffix) {
    case ':t':
      return 'text';
    case ':d':
      return 'date';
    case ':b':
      return 'boolean';
    case ':f':
      return 'float';
    case ':i':
      return 'integer';
  }
  return 'float';
}

export class UdfEditorModel<TState = unknown> extends EditDialogModel<TState> {
  _isNewBuiltIn: DataSignal<boolean>;
  builtInName?: string;

  constructor(public documentType: UdfDocumentType = 'documents') {
    super(
      'User Defined Fields',
      async (ids: string[]) => await udfsSubscriptionService.subscriptionModel.getUdfsByIds(ids),
      DataMutations.insertUdfs,
      DataMutations.updateUdfs,
      DataMutations.deleteUdfs,
      undefined,
      () => mainPageModel.canAddUdfs,
      () => mainPageModel.canEditUdfs,
      () => mainPageModel.canDeleteUdfs,
    );
    this._isNewBuiltIn = property(false);
    this.action('save', this.submit, {
      icon: 'save',
      variant: 'contained',
      color: 'primary',
      disabled: () => this.viewOnly || this.form.hasErrors || (!this.isNewBuiltIn && !this.form.hasChanges),
      type: 'submit',
    });

    requestAnimationFrame(() => {
      if (this.documentType !== 'employees') {
        const expressionField = this.form.field['expression'];
        expressionField.disabled = () => true;
      }
    });
    this.close();
  }

  set isNewBuiltIn(value: boolean) {
    this._isNewBuiltIn(value);
  }

  get isNewBuiltIn() {
    return this._isNewBuiltIn();
  }

  protected get name() {
    return this.form.field.displayName?.value ?? '';
  }

  _reset(defaultObj?: any) {
    super._reset(defaultObj);
    this.isNewBuiltIn = false;
  }

  mapToForm(obj: ObjectType) {
    const o = super.mapToForm(obj);
    if (o.type === 'expression') {
      o.expressionType = udfNameToExpressionType(o.name);
    }
    return o;
  }

  async load(id?: string, isModal: boolean = true) {
    this.id = id;
    this.isModal = isModal;
    this.isNewBuiltIn = false;
    this.builtInName = undefined;

    if (!id) {
      this._reset();
    } else {
      let results = await this._load([id]);
      if (results?.length && results[0]) {
        this.form.value = this.mapToForm(results[0]);
      } else {
        // attempt to load builtIn Udf
        results = udfsSubscriptionService.subscriptionModel.getBuiltInUdfsByIds([id]);
        this.id = undefined; // builtIns need to be saved as new Udfs.
        if (!results?.length || !results[0]) {
          this._reset();
        } else {
          this.isNewBuiltIn = true;
          this.builtInName = results[0].name;
          this.form.value = this.mapToForm(results[0]);
        }
      }
    }

    this.state.title = this.getTitle();
    const typeValue = this.form.field['type'].value;
    this.form.field['allowableValues'].visible = !(
      typeValue === 'reference' ||
      typeValue === 'boolean' ||
      typeValue === 'expression'
    );
    this.form.field['expressionType'].visible = typeValue === 'expression';
    this.form.field['hasEffective'].visible = typeValue === 'reference';
    this.form.field['referenceValues'].visible = typeValue === 'reference';
    this.form.field['expression'].visible = typeValue === 'expression';
    this.form.field['isList'].visible = typeValue !== 'expression';

    this.open();
  }

  mapToSave(): ObjectType {
    const obj = super.mapToSave();
    if (this.isNew) {
      obj.documentType = this.documentType;
      obj.isSystem = this.isNewBuiltIn;
      obj.hasEffective = (obj.type === 'reference' && obj.hasEffective) || false;
      obj.name = this.builtInName;
      if (obj.type === 'expression') {
        if (!obj.expressionType || this.documentType !== 'employees') {
          throw new Error('Expression Type is required for expression UDFs');
        }
        obj.name = `${camelCase(obj.displayName)}${expressionTypesToESType[obj.expressionType as keyof typeof expressionTypesToESType]}`;
        delete obj.expressionType;
      }
      return obj;
    }
    const { id, displayName, allowableValues } = obj;
    return { id, displayName, allowableValues };
  }

  async remove() {
    const result = await super.remove();
    return result;
  }

  async submit() {
    const result = await super.submit();
    return result;
  }

  protected _createForm(): Form {
    const disabled = () => !this.isNew || this.isNewBuiltIn;
    const referenceDisabled = () =>
      (!this.isNew && !this.isNewBuiltIn) || (this.isNewBuiltIn && this.form.field['type'].value === 'reference');
    const udfDocumentTypes = udfsSubscriptionService.subscriptionModel.udfDocumentTypes;
    const udfDocumentTypesSearcher = {
      search(_search?: string) {
        return Object.keys(udfDocumentTypes);
      },
      map(item: any) {
        return (item && udfDocumentTypes[item]) || '';
      },
    };
    return Form.create((form: IFormContext) => ({
      displayName: form
        .string()
        .label('Display Name')
        .validateOnUpdate(true)
        .placeholder('enter display name')
        .validators('required')
        .autoFocus(),
      type: form
        .select(arraySearch(Object.keys(udfTypes), (item: keyof typeof udfTypes) => (item && udfTypes[item]) || ''))
        .validators('required')
        .label('Type')
        .disabled(disabled)
        .placeholder('enter type'),
      isList: form.boolean().disabled(disabled).label('Is it a list of values?'),
      hasEffective: form.boolean().disabled(disabled).label('Do the values expire?'),
      allowableValues: form.string().label('Allowable Values').placeholder('enter a list of allowable values'),
      expression: form
        .custom(createUdfExpressionEditor())
        .disabled(() => false)
        .label('Allowable Values')
        .accessor(
          (obj) => convertUdfsToName(obj?.allowableValues),
          (obj, value) => obj && (obj.allowableValues = convertUdfsToId(value)),
        )
        .placeholder('enter an expression'),
      expressionType: form
        .select(
          arraySearch(Object.keys(expressionTypes), (item: keyof typeof expressionTypes) => (item && expressionTypes[item]) || ''),
        )
        .label('Result Type')
        .disabled(disabled)
        .placeholder('enter the expressions result type'),
      referenceValues: form
        .select(udfDocumentTypesSearcher)
        .label('Allowable Values')
        .disabled(referenceDisabled)
        .accessor(
          (obj) => obj?.allowableValues,
          (obj, value) => obj && (obj.allowableValues = value),
        )
        .placeholder('enter a list of allowable values'),
    }));
  }
}

export default class UdfsModel extends MasterDetailModel {
  public documentType: UdfDocumentType | undefined;
  public constructor(documentType?: UdfDocumentType) {
    super(new UdfEditorModel(documentType));
    this.documentType = documentType;
  }

  open() {
    this.editor!._reset();
    this.load();
    this.activate();
    this.editor!.open();
  }

  get canAdd() {
    return this.editor!.canAdd && this.canAddItem();
  }

  get canDelete() {
    return this.selectedItem && ((this.canAdd && this.editor!.isNew) || (this.editor!.canDelete && !this.editor!.isNew));
  }

  protected _handleOnSubmit() {
    super._handleOnSubmit();
    (this.editor! as UdfEditorModel).isNewBuiltIn = false;
  }

  protected setItems(items: any[]) {
    if (this.selectedItem) {
      const selectedIdx = items.findIndex((i) => i.id === this.selectedItem.id);
      if (selectedIdx >= 0) {
        this.state.selectedItem = Object.assign({}, this.selectedItem, items[selectedIdx]);
        items.splice(selectedIdx, 1, this.selectedItem);
      }
    }
    this.state.items.set(items);
  }

  protected async _selectItem(item: any) {
    await super._selectItem(item);
    this.editor!.visible = !!item;
  }

  async load(): Promise<void> {
    const udfs = udfsSubscriptionService.subscriptionModel.getUdfsByDocumentType(this.documentType).slice();
    this.setItems(udfs);
    this.selectItem();
  }
}

interface IUdfsContainerModelOptions {
  allowableTypes?: UdfType[];
  booleanFieldType?: 'checkbox' | 'select';
  canAdd?: AccessFn;
  canEdit?: AccessFn;
  canDelete?: AccessFn;
}

export function refEquals(x: any) {
  return x.name?.id === this.name?.id && x.start?.date === this.start?.date && x.end?.date === this.end?.date;
}

// this class will extend any editable dialog to add UDF support.
// derive your dialog editor from this class (see DocumentDialogModel and EmployeeDialogModel as examples)
export abstract class UdfsContainerModel<TState = unknown> extends EditDialogModel<TState> {
  private _udfEditor: UdfsModel;
  private watchUdfs: WatcherTypeFn;
  protected _udfs: DataSignal<IUdf[]>;

  protected constructor(
    title: string,
    load: TEditLoadFn,
    insert: TEditSaveFn,
    update: TEditSaveFn,
    remove: TEditDeleteFn,
    protected documentType?: UdfDocumentType,
    protected options?: IUdfsContainerModelOptions,
  ) {
    super(title, load, insert, update, remove, undefined, options?.canAdd, options?.canEdit, options?.canDelete);

    this._udfs = property([]);
    this.watchUdfs = createWatcherFn();
    this.calculateCurrentUdfs();
    this.watchUdfs(
      () => {
        version(udfsSubscriptionService.subscriptionModel.udfs);
      },
      () => {
        this.calculateCurrentUdfs();
      },
    );
  }

  get udfEditor() {
    return this._udfEditor || (this._udfEditor = new UdfsModel(this.documentType));
  }

  set udfs(value: IUdf[]) {
    this.udfs.length = 0;
    this.udfs.push(...value);
  }

  get udfs(): IUdf[] {
    return this._udfs();
  }

  calculateCurrentUdfs() {
    const documentTypeFilter = this.documentType ? [this.documentType] : undefined;
    const currUdfs = udfsSubscriptionService.subscriptionModel.getUdfs({
      documentType: documentTypeFilter,
      type: this.options?.allowableTypes,
    });
    this.calculateFormUdfs(currUdfs, this.udfs);
    this.udfs = currUdfs;
  }

  async load(id?: string, isModal: boolean = true) {
    await super.load(id, isModal);
  }

  mapToForm(obj: ObjectType) {
    const o = super.mapToForm(obj);
    if (!o.fields) return o;
    for (const udf of this.udfs) {
      switch (udf.type) {
        case 'reference': {
          if (!udf.isList || !udf.hasEffective) continue;
          let refs: any[] = o.fields[udf.name];
          if (!Array.isArray(refs)) o.fields[udf.name] = refs = [];
          try {
            for (const ref of refs) {
              if (ref.id) {
                ref.name = { id: ref.id, name: ref.name, code: ref.code };
              } else {
                delete ref.name;
              }
              ref.start = ref.start ? utc(ref.start) : utc(UTC.MinValue);
              ref.end = ref.end ? utc(ref.end) : utc(UTC.MaxValue);
              ref.equals = refEquals;
              delete ref.id;
              delete ref.code;
            }
          } catch (err) {
            o.fields[udf.name] = refs = [];
            break;
          }
        }
      }
    }
    return o;
  }

  mapToSave() {
    const o = super.mapToSave();
    if (!o.fields) return o;
    for (const udf of this.udfs) {
      switch (udf.type) {
        case 'reference': {
          if (!udf.isList || !udf.hasEffective) continue;
          const refs: any[] = o.fields[udf.name];
          if (!Array.isArray(refs)) continue;
          const result: any[] = [];
          for (const ref of refs) {
            if (ref.name?.id) {
              result.push(Object.assign({}, ref, ref.name));
            }
          }
          o.fields[udf.name] = result;
        }
      }
    }
    return o;
  }

  calculateFormUdfs(currUdfs: IUdf[], oldUdfs: IUdf[]) {
    const toAdd = [...currUdfs];
    if (oldUdfs?.length) {
      const toRemove = [] as string[];
      for (const oldUdf of oldUdfs) {
        const idx = toAdd.findIndex(
          (udf) => udf.id === oldUdf.id && udf.displayName === oldUdf.displayName && udf.allowableValues === oldUdf.allowableValues,
        );
        if (idx >= 0) {
          toAdd.splice(idx, 1);
        } else {
          toRemove.push(oldUdf.name);
        }
      }
      this.form.removeFields(toRemove);
    }

    if (!toAdd.length) return;
    this.addUdfFormFields(toAdd);
  }

  addUdfFormFields(udfs: IUdf[]) {
    this.form.addFields((_) => {
      const fields: IFieldDefns = {};
      const disabled = () => this.viewOnly;
      const boolSearcher = arraySearch([true, false], (item: any) => (!isNullOrUndefined(item) ? (item ? 'Yes' : 'No') : ''));
      for (const udf of udfs) {
        switch (udf.type) {
          case 'text':
            {
              let field: IFieldDefn;
              if (udf.isList) {
                const search = udf.allowableValues
                  ? createUdfSearch(udf, (v) => v.name)
                  : arraySearch([], (v) => (Array.isArray(v) ? '' : v || ''));
                const inputAdd = udf.allowableValues ? undefined : (v: string) => v;
                field = _.multiselectautocomplete(search, { chipLimit: 2, inputAdd: inputAdd });
              } else {
                field = udf.allowableValues ? _.select(createUdfSearch(udf, (v) => v.name)) : _.string();
              }
              fields[udf.name] = field
                .accessor(['fields', udf.name])
                .label(udf.displayName)
                .placeholder(`enter ${udf.displayName}`)
                .disabled(disabled);
            }
            break;
          case 'date':
            fields[udf.name] = _.date()
              .accessor(
                (obj) => obj?.fields?.[udf.name],
                (obj, value) => {
                  if (!obj) return;
                  if (!obj.fields) obj.fields = {};
                  const newValue = value && (value instanceof UTC ? value : utc(value.toString()));
                  if (newValue) {
                    obj.fields[udf.name] = newValue;
                  } else {
                    delete obj.fields[udf.name];
                  }
                },
              )
              .label(udf.displayName)
              .placeholder(`enter ${udf.displayName}`)
              .disabled(disabled);
            break;
          case 'float':
            fields[udf.name] = _.number({ thousandSeparator: true, decimals: 4 })
              .accessor(['fields', udf.name])
              .label(udf.displayName)
              .placeholder(`enter ${udf.displayName}`)
              .disabled(disabled);
            break;
          case 'integer':
            fields[udf.name] = _.number({ thousandSeparator: true, decimals: 0 })
              .accessor(['fields', udf.name])
              .label(udf.displayName)
              .placeholder(`enter ${udf.displayName}`)
              .disabled(disabled);
            break;
          case 'expression':
            fields[udf.name] = _.static()
              .accessor(['fields', udf.name])
              .label(udf.displayName)
              .disabled(() => true);
            break;
          case 'boolean':
            fields[udf.name] =
              this.options?.booleanFieldType === 'select'
                ? _.select(boolSearcher)
                    .accessor(['fields', udf.name])
                    .label(udf.displayName)
                    .placeholder(`enter ${udf.displayName}`)
                    .disabled(disabled)
                : _.boolean()
                    .accessor(['fields', udf.name])
                    .label(udf.displayName)
                    .placeholder(`enter ${udf.displayName}`)
                    .disabled(disabled);
            break;
          case 'reference':
            {
              const searcher = createUdfSearch(udf);
              if (udf.isList && udf.hasEffective) {
                fields[udf.name] = _.custom(createReferenceListWithEffectiveRange(searcher, udf))
                  .accessor(['fields', udf.name])
                  .label(udf.displayName)
                  .placeholder(`enter ${udf.displayName}`)
                  .updateOnChange(true)
                  .disabled(disabled);
                break;
              }
              const searchField = udf.isList
                ? _.multiselectautocomplete(searcher, { chipLimit: 2 })
                : _.select(searcher).editor('auto-complete');
              fields[udf.name] = searchField
                .accessor(['fields', udf.name])
                .label(udf.displayName)
                .placeholder(`enter ${udf.displayName}`)
                .disabled(disabled);
              if (udf.hasEffective) {
                _.date()
                  .accessor(['fields', udf.name, 'start'])
                  .label(`${udf.displayName} Start`)
                  .placeholder(`enter ${udf.displayName} start date`)
                  .disabled(disabled);
                _.date()
                  .accessor(['fields', udf.name, 'end'])
                  .label(`${udf.displayName} End`)
                  .placeholder(`enter ${udf.displayName} end date`)
                  .disabled(disabled);
              }
            }
            break;
        }
      }
      return fields;
    });
  }
}
